import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import moment from "moment";
import { Table, Container, Modal, ModalHeader, ModalBody, Input, Button } from "reactstrap";
import { QRCodeCanvas } from "qrcode.react";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaSort, FaSortUp, FaSortDown, FaEye } from "react-icons/fa";
import * as XLSX from "xlsx";

import './DataPengguna.css';

const DataPengguna = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [qrData, setQrData] = useState("");
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: "date_created", direction: "descending" });
  const tableRef = useRef(null);

  // Fetch data dari API
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get("https://rlagency.id/app/api_gathering_upi/get_data_gathering.php");

      const updatedData = response.data.map((user) => {
        if (user.updated_position) {
          const coords = parseWKT(user.updated_position);
          return {
            ...user,
            google_maps_link: coords ? `https://www.google.com/maps?q=${coords.lat},${coords.lon}` : "",
          };
        }
        return { ...user, google_maps_link: "" };
      });

      setData(updatedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const parseWKT = (wkt) => {
    if (!wkt) return null;
    const match = wkt.match(/POINT\((-?\d+\.\d+) (-?\d+\.\d+)\)/);
    return match ? { lat: match[2], lon: match[1] } : null;
  };

  const handleSort = (column) => {
    let direction = "ascending";
    if (sortConfig.key === column && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key: column, direction });
  };

  const filteredData = data.filter((user) =>
    Object.keys(filters).every((column) => {
      const filterValue = filters[column]?.toLowerCase().trim();
      const columnValue = user[column]?.toString().toLowerCase() || "";
      return filterValue ? columnValue.includes(filterValue) : true;
    })
  );

  const sortedAndFilteredData = [...filteredData].sort((a, b) => {
    const valA = a[sortConfig.key]?.toString().toLowerCase() || "";
    const valB = b[sortConfig.key]?.toString().toLowerCase() || "";

    if (sortConfig.key.includes("date")) {
      return sortConfig.direction === "ascending"
        ? moment(valA).diff(moment(valB))
        : moment(valB).diff(moment(valA));
    }

    return valA.localeCompare(valB) * (sortConfig.direction === "ascending" ? 1 : -1);
  });

  const toggleModal = (qrCode) => {
    setQrData(qrCode);
    setModal(!modal);
  };

  const handleFilterChange = (column, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [column]: value.toLowerCase().trim(),
    }));
  };

  const handleCheckIn = async (email, whatsapp, userId) => {
    const scrollPosition = tableRef.current ? tableRef.current.scrollTop : 0;

    try {
      const response = await axios.post("https://rlagency.id/app/api_gathering_upi/self_check_in.php", {
        email,
        whatsapp,
      });

      if (response.data.success) {
        const updatedDate = moment().toISOString();
        setData((prevData) =>
          prevData.map((user) =>
            user.id === userId ? { ...user, date_updated: updatedDate } : user
          )
        );
        fetchData();
        alert("Check-in berhasil!");
      } else {
        alert("Check-in gagal. Coba lagi.");
      }
    } catch (error) {
      console.error("Error sending check-in:", error);
      alert("Terjadi kesalahan saat mengirim data.");
    } finally {
      if (tableRef.current) {
        tableRef.current.scrollTop = scrollPosition;
      }
    }
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "DataPengguna");
    XLSX.writeFile(workbook, "Data_Pengguna.xlsx");
  };

  return (
    <Container fluid className="data-pengguna-container">
      <div className="data-pengguna-header">
        <h3>Pendaftaran Gathering</h3>
        <div className="data-header-actions">
          <span className="data-count">Count : {filteredData.length}</span>
          <Button color="primary" onClick={exportToExcel}>Download Excel</Button>
        </div>
      </div>

      {loading ? (
        <div className="data-pengguna-loading">Loading...</div>
      ) : (
        <div className="data-pengguna-table-wrapper" >
        <Table striped hover bordered className="data-pengguna-table" ref={tableRef}>
          <thead >
            <tr>
              <th>No.</th>
              {["nama", "whatsapp", "email", "jenis_kelamin", "qr", "status_pekerjaan", "fakultas", "jabatan_posisi", "date_updated"].map((col) => (
                <th key={col}>
                  <div className="d-flex justify-content-between align-items-center">
                    <span>{col.replace("_", " ").toUpperCase()}</span>
                    <button className="btn btn-link p-0" onClick={() => handleSort(col)}>
                      {sortConfig.key === col ? (
                        sortConfig.direction === "ascending" ? <FaSortUp /> : <FaSortDown />
                      ) : (
                        <FaSort />
                      )}
                    </button>
                  </div>
                </th>
              ))}
              <th>Action</th>
            </tr>
            <tr>
              <td></td>
              {["nama", "whatsapp", "email", "jenis_kelamin", "qr_code_data", "status_pekerjaan", "fakultas", "jabatan_posisi", "date_updated"].map((col) => (
                <td key={`filter-${col}`}>
                  <Input type="text" placeholder={`Filter ${col}`} onChange={(e) => handleFilterChange(col, e.target.value)} />
                </td>
              ))}
              <td></td>
            </tr>
          </thead>
          <tbody>
            {sortedAndFilteredData.map((user, index) => (
              <tr key={user.id}>
                <td>{index + 1}</td>
                <td>{user.nama}</td>
                <td>{user.whatsapp}</td>
                <td>{user.email}</td>
                <td>{user.jenis_kelamin}</td>
                <td>
                  {user.qr_code_data && <Button color="primary" size="sm" onClick={() => toggleModal(user.qr_code_data)}><FaEye /></Button>}
                </td>
                <td>{user.status_pekerjaan}</td>
                <td>{user.fakultas}</td>
                <td>{user.jabatan_posisi}</td>
                <td>{user.date_updated ? moment(user.date_updated).format("HH:mm - DD/MM/YYYY") : ""}</td>
                <td><Button color="success" size="sm" onClick={() => handleCheckIn(user.email, user.whatsapp, user.id)}>Update</Button></td>
              </tr>
            ))}
          </tbody>
        </Table>
        </div>
      )}

      <Modal isOpen={modal} toggle={() => setModal(!modal)}>
        <ModalHeader toggle={() => setModal(!modal)}>QR Code</ModalHeader>
        <ModalBody className="text-center">
          {qrData && <QRCodeCanvas value={qrData} size={200} />}
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default DataPengguna;
